import { CheckIcon, CopyIcon } from "lucide-react"
import { Button } from "@/primitives/button.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { useCopied } from "./key.tsx"

/*
 * Text a Member copies into something else exactly as it stands: a command to run, a URL to paste,
 * a file's entry. It sits in a sunken well in monospace, scrolling sideways rather than wrapping so
 * nothing is copied broken, with one button that copies it whole.
 */

/**
 * `value` is the text, copied whole; `shape` is `line` for a command or a URL, `block` for text of
 * several lines, which keeps its line breaks and indentation. `copies` names it for the copy
 * button, as "Copy the command".
 */
export function CodeSnippet({
  value,
  shape,
  copies,
}: {
  value: string
  shape: "line" | "block"
  copies: string
}) {
  const { copied, copy } = useCopied(value)
  return (
    <div
      className={cn(
        "flex gap-2 rounded-md bg-surface-sunken py-1 pr-1 pl-3 ring-1 ring-border-subtle ring-inset",
        shape === "line" ? "items-center" : "items-start",
      )}
    >
      <pre className="min-w-0 flex-1 overflow-x-auto py-1 font-mono text-xs text-foreground">
        <code>{value}</code>
      </pre>
      <Tooltip>
        <TooltipTrigger
          closeOnClick={false}
          onClick={() => void copy()}
          render={<Button variant="ghost" size="icon-xs" aria-label={`Copy ${copies}`} />}
        >
          {copied ? <CheckIcon aria-hidden /> : <CopyIcon aria-hidden />}
        </TooltipTrigger>
        <TooltipContent>{copied ? "Copied" : `Copy ${copies}`}</TooltipContent>
      </Tooltip>
    </div>
  )
}
